import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CellProps } from 'react-table'
import { useSelector } from 'react-redux'
import Pen from 'assets/icons/camera-page/Pen'
import Bin from 'assets/icons/camera-page/Bin'
import ClickableRowArrow from 'assets/icons/camera-page/ClickableRowArrow'
import DeleteSalesOrder from './delete-sales-order/DeleteSalesOrder'
import Chip from 'libs/chip/Chip'
import SidePanel from 'libs/sidepanel/SidePanel'
import Popup from 'libs/popup/Popup'
import MotionDiv from 'libs/motionDiv'
import useTabTitle from 'libs/hooks/useTabTitle'
import FilterableSearchTable from 'libs/table/filterable-search-table_new/FilterableSearchTable'
import { hasAccess } from 'utils/utils'
import { formatUnixDate } from 'utils/dateTimeHandlers'
import { ExtendedColumn } from 'store/services/utilsTypes'
import { selectSelectedItem } from 'store/redux/navSlice'
import ViewSOProducts from './panel/ViewSOProducts'
import { SalesOrder } from './sales-order-endpoints/salesOrdersTypes'
import FilterSalesOrder from './panel/FilterSalesOrder'
import { useGetAllSalesOrdersQuery } from './sales-order-endpoints/salesOrders.endpoints'

const additionalFilters = {
  created_between: [],
  updated_between: [],
  status: [],
  category: [],
  sort_by: 'created_on',
  sort_dir: 'DESC',
}

const SalesOrders = () => {
  useTabTitle('Sales Orders')
  const plantId = useSelector(selectSelectedItem)

  const navigate = useNavigate()

  const hasViewAccess = hasAccess('ord', { so: 'v' })
  const hasEditAccess = hasAccess('ord', { so: 'e' })
  const [selectedSalesOrder, setSelectedSalesOrder] = useState<{
    showPanel: boolean
    salesOrder: SalesOrder | null
  }>({
    showPanel: false,
    salesOrder: null,
  })

  const handleSelectedProduct = (salesOrder: SalesOrder) => {
    if (salesOrder) {
      setSelectedSalesOrder({
        showPanel: true,
        salesOrder: salesOrder,
      })
    }
  }
  const [deletePopUp, setDeletePopUp] = useState<{
    isPopUpOpen: boolean
    salesOrder: SalesOrder | null
  }>({
    isPopUpOpen: false,
    salesOrder: null,
  })

  const columns: ExtendedColumn<SalesOrder>[] = [
    {
      Header: 'Sales Order ID',
      accessor: 'salesOrderID',
      width: 150,
      Cell: ({ row }: CellProps<SalesOrder>) => {
        const data = row?.original
        return (
          <div
            className={`${hasViewAccess ? 'hyperlink' : ''}`}
            onClick={() => {
              hasViewAccess &&
                data?.id &&
                navigate(`/orders/sales-order/view/${data.id}`)
            }}
          >
            {data?.salesOrderID ?? '-'}
          </div>
        )
      },
    },
    {
      Header: (
        <div className="flex w-full items-center justify-end">
          Customer Name
        </div>
      ),
      accessor: 'customer',
      width: 150,
      Cell: ({ row }: CellProps<SalesOrder>) => {
        return <div>{row.original.customer?.name ?? '-'}</div>
      },
    },
    {
      Header: (
        <div className="flex w-full items-center justify-end">Products</div>
      ),
      accessor: 'products',
      width: 150,
      Cell: ({ row }: CellProps<SalesOrder>) => {
        const salesOrder = row?.original
        return (
          <div
            className={`${hasViewAccess ? 'hyperlink' : ''}`}
            onClick={() => handleSelectedProduct(salesOrder)}
          >
            {salesOrder?.products?.length ?? '-'} Products
          </div>
        )
      },
      disableSortBy: true,
    },
    {
      Header: (
        <div className="flex w-full items-center justify-end">
          Delivery Date
        </div>
      ),
      accessor: 'deliveryDate',
      width: 150,
      Cell: ({ row }: CellProps<SalesOrder>) =>
        formatUnixDate(row.original.deliveryDate),
    },
    {
      Header: (
        <div className="flex w-full items-center justify-end">Status</div>
      ),
      accessor: 'status',
      width: 50,
      Cell: ({ row }: CellProps<SalesOrder>) => (
        <Chip title={row.original.status} />
      ),
      disableSortBy: true,
    },
    {
      Header: 'Actions',
      accessor: 'id',
      width: 10,
      Cell: ({ row }: CellProps<SalesOrder>) => (
        <div className="flex w-full items-center justify-between">
          <div className="flex flex-row">
            <button
              className={`mr-2 ${hasEditAccess ? '' : 'opacity-40'} `}
              onClick={(e) => {
                e.stopPropagation()
                hasEditAccess &&
                  row.original.id &&
                  navigate(`edit/${row.original.id}`)
              }}
            >
              <Pen />
            </button>
            <button
              className={`mr-2 ${hasEditAccess ? '' : 'opacity-40'} `}
              onClick={(event) => {
                event.stopPropagation()
                hasEditAccess &&
                  setDeletePopUp({
                    isPopUpOpen: true,
                    salesOrder: row?.original,
                  })
              }}
            >
              <Bin />
            </button>
          </div>

          <span
            className="cursor-pointer"
            onClick={() =>
              hasViewAccess &&
              row?.original?.id &&
              navigate(`/orders/sales-order/view/${row?.original.id}`)
            }
          >
            <ClickableRowArrow />
          </span>
        </div>
      ),
      disableSortBy: true,
    },
  ]

  const handleAddSalesOrder = () => {
    hasEditAccess && navigate('new-sales-order')
  }

  return (
    <MotionDiv>
      <>
        <h1 className="h4-bold-black">Sales Orders</h1>
        <p className="p-sm-regular-1">
          View the details of your added sales orders and easily add new ones as
          needed.
        </p>
        <FilterableSearchTable
          useQuery={useGetAllSalesOrdersQuery}
          columns={columns}
          addButton={{ label: 'Add Sales Order', onClick: handleAddSalesOrder }}
          placeholders={{
            emptyMessage: 'No Sales orders are added. Please add Sales order',
            filterEmptyMessage: 'No matching results found',
            search: 'Search by sales order ID, status or customers',
          }}
          filterPanelComponent={FilterSalesOrder}
          additionalFilters={additionalFilters}
          accessRights={hasEditAccess}
        />
        {deletePopUp.salesOrder && (
          <Popup
            isOpen={deletePopUp.isPopUpOpen}
            title="Delete Sales Order"
            onClose={() => {
              setDeletePopUp({ isPopUpOpen: false, salesOrder: null })
            }}
          >
            <DeleteSalesOrder
              closePopUp={() =>
                setDeletePopUp({ isPopUpOpen: false, salesOrder: null })
              }
              salesOrder={deletePopUp.salesOrder}
              plantId={plantId?.id}
            />
          </Popup>
        )}
        {selectedSalesOrder?.salesOrder && (
          <SidePanel
            size="large"
            isOpen={selectedSalesOrder.showPanel}
            title={'Product Details'}
            onClose={() => {
              setSelectedSalesOrder((prev) => ({
                ...prev,
                showPanel: false,
                data: null,
              }))
            }}
          >
            <ViewSOProducts
              isOpen={selectedSalesOrder.showPanel}
              closePanel={() => {
                setSelectedSalesOrder((prev) => ({
                  ...prev,
                  showPanel: false,
                  data: null,
                }))
              }}
              products={selectedSalesOrder.salesOrder.products}
              salesOrderID={selectedSalesOrder.salesOrder.salesOrderID}
            />
          </SidePanel>
        )}
      </>
    </MotionDiv>
  )
}

export default SalesOrders
